import { useMutation } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Input,
  Text,
} from "@chakra-ui/react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { serverGql } from "__generated__/serverGql";
import LoginLayout from "components/Layout/LoginLayout";
import HeadTitle from "components/Shared/HeadTitle";
import serverClient from "utils/apollo/serverClient";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import Form from "components/Shared/Forms/Form";
import LoginLayoutButton from "components/Layout/LoginLayoutButton";

export const SEND_RESET_EMAIL = serverGql(/* ServerGraphQL */ `
  mutation SendResetEmail($email: String!) {
    resetPassword(email: $email) {
      success
      errors
    }
  }
`);

type FormFields = {
  email: string;
};

const schema: yup.ObjectSchema<FormFields> = yup.object().shape({
  email: yup.string().trim().email().required().label("Email"),
});

const { FormField } = createFormFieldComponents<FormFields>({
  schema,
});

export default function ForgotPassword() {
  const [sendResetEmail, response] = useMutation(SEND_RESET_EMAIL, {
    client: serverClient,
  });

  const formContext = useForm<FormFields>({
    resolver: yupResolver(schema),
  });

  const { formState, watch } = formContext;
  const { isValid } = formState;
  const email = watch("email");

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (data: FormFields) => sendResetEmail({ variables: data }),
    { isSuccessToasted: false },
  );

  return (
    <Form {...{ formContext }} onValidSubmit={handleValidSubmit}>
      <HeadTitle title="Forgot Password" />

      {response.called && !response.error && !response.loading ? (
        <Text mt={9} textAlign="center" mb={2} color="magenta.veryDark">
          If an account exists with the email <b>{email}</b>, a link has been
          sent to reset the password associated with that account. Please check
          your inbox.
        </Text>
      ) : (
        <>
          <FormField
            name="email"
            id="email"
            label={null}
            placeholder="Enter email"
            autoComplete="email"
            autoFocus
            renderInput={(props) => (
              <Input
                type="email"
                autoFocus
                size="lg"
                backgroundColor="white"
                {...props}
              />
            )}
            mb={0}
          />

          {response.error && (
            <Alert status="error" mt={5} variant="left-accent">
              <AlertIcon />
              <AlertDescription>{response.error?.message}</AlertDescription>
            </Alert>
          )}

          <LoginLayoutButton
            type="submit"
            mt={6}
            aria-disabled={!isValid}
            isLoading={response.loading}
          >
            Submit
          </LoginLayoutButton>
        </>
      )}
    </Form>
  );
}

ForgotPassword.getLayout = (page: React.ReactElement) => (
  <LoginLayout
    heading="Forgot Password?"
    subheading="Enter the email associated with your account, and we’ll send reset instructions."
  >
    {page}
  </LoginLayout>
);
